import React, { useMemo } from 'react';
import { Alert, Button, Space, Typography } from 'antd';
import { Edit } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import type { OutputData } from '@editorjs/editorjs';

import RichTextEditor from '../../../components/editor/RichTextEditor';
import RichTextView from '../../../components/editor/RichTextView';
import type { MentionUser } from '../../../components/editor/RichTextEditor';
import { isLegacyEditor } from '../../../components/editor/editorFlag';
import { containsInlineImage, isEditorJsJson, isRichTextEmpty } from '../../../components/editor/richText';
import LegacyRichTextEditor from '../../../components/elements/RichTextEditor';
import LegacyReadOnlyEditor from '../../../components/elements/ReadOnlyRichTextEditor';
import { isInlineImagesNotAllowedError } from '../../../stores/kanban/TaskStore';
import type { TaskStore } from '../../../stores/kanban/TaskStore';
import type { KanbanTask } from '../../../stores/KanbanTaskStores';
import { safeParseEditorData } from '../useTaskHelpers';
import { useDirtyGuard } from './DirtyGuard';
import { useTaskField } from './useTaskField';

const { Text } = Typography;

/**
 * Content-only view of an Editor.js document: `time`, `version` and block ids change on every
 * `save()` and an empty document is written as a single empty paragraph, so comparing the raw JSON
 * would report a change the user never made.
 */
function legacyContentKey(raw: string): string {
  const parsed = safeParseEditorData(raw);
  const blocks: Array<{ type?: string; data?: unknown }> = Array.isArray(parsed?.blocks) ? parsed.blocks : [];
  const meaningful = blocks
    .map((block) => ({ type: block?.type ?? '', data: JSON.stringify(block?.data ?? {}) }))
    .filter((block) => block.data !== '{}' && block.data !== '{"text":""}');
  return JSON.stringify(meaningful);
}

export type TaskDescriptionProps = {
  task: KanbanTask;
  store: TaskStore;
  readOnly: boolean;
  mentionUsers: MentionUser[];
};

/**
 * The description block of the drawer's main column.
 *
 * Editing is left only by Save or Cancel — clicking away never closes the editor, so a draft can
 * not be lost by a stray click, and a rejected save keeps the text on screen for a retry.
 */
const TaskDescription: React.FC<TaskDescriptionProps> = observer(
  ({ task, store, readOnly, mentionUsers }) => {
    const { t } = useTranslation();

    const description = task.description ?? '';

    // The legacy editor only understands Editor.js JSON. A description already stored as HTML has
    // to keep using TipTap even while this browser is pinned to the old editor, or opening it would
    // show an empty document and saving would overwrite the text with `{"blocks":[]}`.
    const legacy = useMemo(
      () => isLegacyEditor() && (!description || isEditorJsJson(description)),
      [description],
    );

    const field = useTaskField<string>({
      identity: task.id,
      value: description,
      save: (html) => store.updateTaskDescription(task.id, html),
      successMessage: t('DescriptionUpdated'),
      errorMessage: t('FailedToUpdateDescription'),
      // The store already surfaces this one with its own explanation.
      isSilentError: isInlineImagesNotAllowedError,
    });

    const isEmpty = isRichTextEmpty(description);

    // TipTap refuses base64 images, so opening such a description and saving it would silently drop
    // the pictures. Until an administrator has run the image cleanup this one stays read-only.
    const hasInlineImages = !legacy && containsInlineImage(description);
    const canEdit = !readOnly && !hasInlineImages;

    useDirtyGuard('task-description', field.dirty);

    const legacyDraft = useMemo<OutputData>(
      () => safeParseEditorData(field.draft) as OutputData,
      // Only the value the editor was opened with matters — Editor.js owns the document afterwards.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [field.editing],
    );

    const footer = (
      <Space className="task-description__actions">
        <Button
          type="primary"
          onClick={() => void field.commit()}
          loading={field.saving}
          disabled={!field.dirty}
        >
          {t('Save')}
        </Button>
        <Button onClick={field.cancel} disabled={field.saving}>
          {t('Cancel')}
        </Button>
      </Space>
    );

    return (
      <section className="task-description">
        <div className="task-description__head">
          <Text className="task-drawer__section-title">{t('Description')}</Text>
          {canEdit && !field.editing ? (
            <Button
              type="text"
              size="small"
              icon={<Edit size={14} />}
              className="task-description__edit"
              onClick={field.startEdit}
            >
              {t('Edit')}
            </Button>
          ) : null}
        </div>

        {hasInlineImages ? (
          <Alert
            type="warning"
            showIcon
            className="task-description__notice"
            message={t('taskDrawerInlineImagesNotice')}
          />
        ) : null}

        {field.editing ? (
          <div className="task-description__editing">
            {legacy ? (
              <div className="editor-frame">
                <LegacyRichTextEditor
                  initialData={legacyDraft}
                  onChange={(data: OutputData) => {
                    const next = JSON.stringify(data);
                    // Editor.js fires `onChange` while it initialises (an empty document gains an
                    // empty paragraph), which would arm Save before anything was typed. Keep the
                    // stored value as the draft until the content really differs from it.
                    const changed = legacyContentKey(next) !== legacyContentKey(description);
                    field.setDraft(changed ? next : description);
                  }}
                />
              </div>
            ) : (
              <RichTextEditor
                value={field.draft}
                onChange={field.setDraft}
                onSave={(html) => void field.commit(html)}
                autoFocus
                minHeight={220}
                placeholder={t('taskDrawerAddDescription')}
                uploadImage={(file) => store.uploadTaskImage(task.id, file)}
                mentionUsers={mentionUsers}
                className="task-description__editor"
              />
            )}
            {footer}
          </div>
        ) : (
          <div
            className={`task-description__view${canEdit ? ' is-editable' : ''}`}
            role={canEdit ? 'button' : undefined}
            tabIndex={canEdit ? 0 : undefined}
            onClick={canEdit ? field.startEdit : undefined}
            onKeyDown={
              canEdit
                ? (event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      field.startEdit();
                    }
                  }
                : undefined
            }
          >
            {isEmpty ? (
              <Text type="secondary" className="task-description__empty">
                {canEdit ? t('taskDrawerAddDescription') : t('NoDescription')}
              </Text>
            ) : legacy ? (
              <LegacyReadOnlyEditor data={safeParseEditorData(description)} taskId={task.id} disabled />
            ) : (
              <RichTextView value={description} />
            )}
          </div>
        )}
      </section>
    );
  },
);

export default TaskDescription;
